import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Subject, distinctUntilChanged } from 'rxjs';
import { AttendanceHistoryAdapterService } from '../adapters/attendance-history-response.adpater';
import { Attendance } from '../interfaces/attendance';
import { BlockContactService } from './block-contact.service';
import { CompanyBlockDataService } from 'src/app/core/services/company-block-data.service';
import { AttendanceMessage, AttendanceMessageType, EventTypeHistory, HistoriesWithEvent } from '../interfaces/attendance-list-history.interface';
import { BehaviorSubjectWithInterceptors } from 'src/app/shared/classes/behavior-subject-with-interceptors';
import {PhoneStatus} from "../interfaces/phone-status.interface";
import {CallStatus} from "@core/enums/call-status.enum";
import { CtaUnreadAttendanceTitleService } from './cta-unread-attendance-title.service';
import { CompanyAccessService } from '@core/services/company-access.service';
import { FeatureToggleService } from '@core/services/feature-toggle.service';
import { FeatureToggles } from '@core/enums/feature-toggles.enum';

@Injectable({
  providedIn: 'root',
})
export class LiveChatStateService {
  //Private properties
  public isWriting = false;
  public transferBoxCurrentViewMode = 'user';
  //Subjects
  private readonly socketConnectionIdSub: Subject<string | undefined> =
    new Subject();
  //Observables
  public readonly socketConnectionId$ =
    this.socketConnectionIdSub.asObservable();
  private readonly attendancesSub = new BehaviorSubject<Attendance[]>([]);
  public readonly attendances$ = this.attendancesSub.asObservable();
  private readonly currentAttendanceSub = new BehaviorSubjectWithInterceptors<
    Attendance | undefined
  >(undefined);
  public readonly listenCurrentAttendance$ =
    this.currentAttendanceSub.asObservable();
  public readonly listenCurrentAttendaceChanged$ =
    this.currentAttendanceSub.pipe(
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id)
    );
  private readonly userHasWhatsappPermissionSub = new BehaviorSubject(false);
  private readonly userHasWhatsappNumberPermissionSub = new BehaviorSubject(false);
  private readonly languagesCompanySub = new BehaviorSubject<any>( undefined );
  private readonly userHasPhonePermissionSub = new BehaviorSubject(false);
  private readonly phoneAppStatus = new BehaviorSubject<PhoneStatus | undefined>(undefined);
  private readonly phoneCallStatus = new BehaviorSubject<CallStatus | undefined>(undefined);
  //Public properties
  public readonly userHasWhatsappPermission$ =
    this.userHasWhatsappPermissionSub.asObservable();
  public readonly userHasPhonePermission$ =
    this.userHasPhonePermissionSub.asObservable();
  private readonly userHasOnlyOneCompanySub = new BehaviorSubject(false);
  public readonly userHasOnlyOneCompany$ =
    this.userHasOnlyOneCompanySub.asObservable();
  private readonly appBarCollapsedSub = new BehaviorSubject(true);
  public readonly appBarCollapsed$ = this.appBarCollapsedSub.asObservable();
  private readonly blockContactFeatureEnabledSub = new BehaviorSubject(false);
  public readonly blockContactFeatureEnabled$ = this.blockContactFeatureEnabledSub.asObservable();
  private readonly blockedListRefreshSub = new Subject<void>();
  public readonly blockedListRefresh$ = this.blockedListRefreshSub.asObservable();
  private readonly blockAttendanceExportSub = new BehaviorSubject(true);
  public readonly blockAttendanceExport$ = this.blockAttendanceExportSub.asObservable();
  private readonly isExportingPdfSub = new BehaviorSubject(false);
  public readonly isExportingPdf$ = this.isExportingPdfSub.asObservable();
  private readonly kanbanWaitingTimeEnabledSub = new BehaviorSubject(true);
  public readonly kanbanWaitingTimeEnabled$ = this.kanbanWaitingTimeEnabledSub.asObservable();
  private readonly connectionStatusSub = new BehaviorSubject<string | null>(null);
  public readonly connectionStatus$ = this.connectionStatusSub.asObservable();
  private readonly connectionEstablishedSub = new BehaviorSubject<boolean>(false);
  public readonly connectionEstablished$ = this.connectionEstablishedSub.asObservable();
  private readonly currentHistorySub = new BehaviorSubject<AttendanceMessage[]>(
    []
  );
  public readonly currentHistory$ = this.currentHistorySub.asObservable();
  private readonly currentHistoriesSub = new BehaviorSubject<HistoriesWithEvent>
  ({ histories: [], event: EventTypeHistory.OLD_MESSAGES });
  public readonly currentHistories$ = this.currentHistoriesSub.asObservable();
  private readonly chatNoAccessSub = new BehaviorSubject<boolean>(false);
  public readonly listenChatNoAccess$ = this.chatNoAccessSub.asObservable();
  private readonly attendanceToLoad$ = new BehaviorSubject<{type: 'previous' | 'next', attendanceId: string, page?: number} | undefined>(undefined);
  public readonly currentArchivedAttendanceId$ =
    this.attendanceToLoad$.asObservable();
  public readonly currentPhoneAppStatus$ =
    this.phoneAppStatus.asObservable();
  public readonly currentPhoneCallStatus$ =
    this.phoneCallStatus.asObservable();

  //Values

  constructor(
    private injector: Injector,
  ) {
    this.listenBlockDataChanges();
  }

  private get blockContactService(): BlockContactService {
    return this.injector.get(BlockContactService);
  }

  private get companyBlockDataService(): CompanyBlockDataService {
    return this.injector.get(CompanyBlockDataService);
  }

  private get featureToggleService(): FeatureToggleService {
    return this.injector.get(FeatureToggleService);
  }

  private get companyAccessService(): CompanyAccessService {
    return this.injector.get(CompanyAccessService);
  }

  private listenBlockDataChanges(): void {
    this.injector.get(CompanyBlockDataService).onBlockDataUpdated$
      .subscribe(() => {
        this.refreshAllBlockedStates();
      });
  }

  private enrichWithBlockedState(attendance: Attendance): Attendance {
    const isBlocked = attendance.currentContactChannel?.isBlocked === true;
    return { ...attendance, isContactBlocked: isBlocked };
  }

  private refreshAllBlockedStates(): void {
    const attendances = this.attendances;
    if (!attendances.length) return;

    let changed = false;
    const updated = attendances.map(a => {
      const isBlocked = a.currentContactChannel?.isBlocked === true;
      if (a.isContactBlocked !== isBlocked) {
        changed = true;
        return { ...a, isContactBlocked: isBlocked };
      }
      return a;
    });

    if (changed) {
      this.attendancesSub.next(updated);

      if (this.currentAttendance) {
        const current = updated.find(a => a.id === this.currentAttendance!.id);
        if (current && current.isContactBlocked !== this.currentAttendance.isContactBlocked) {
          this.currentAttendanceSub.next({ ...this.currentAttendance, isContactBlocked: current.isContactBlocked });
        }
      }
    }
  }

  public setAttendanceBlockedState(attendanceId: string, isBlocked: boolean): void {
    const idx = this.attendances.findIndex(a => a.id === attendanceId);
    if (idx >= 0) {
      const updated = [...this.attendances];
      updated[idx] = { ...updated[idx], isContactBlocked: isBlocked };
      this.attendancesSub.next(updated);
    }

    if (this.currentAttendance?.id === attendanceId) {
      this.currentAttendanceSub.next({ ...this.currentAttendance, isContactBlocked: isBlocked });
    }

    this.blockedListRefreshSub.next();
  }

  public setContactChannelBlockedState(attendanceId: string, isBlocked: boolean): void {
    const idx = this.attendances.findIndex(a => a.id === attendanceId);
    if (idx >= 0) {
      const updated = [...this.attendances];
      const attendance = updated[idx];
      updated[idx] = {
        ...attendance,
        currentContactChannel: { ...attendance.currentContactChannel, isBlocked },
        isContactBlocked: isBlocked,
      };
      this.attendancesSub.next(updated);
    }

    if (this.currentAttendance?.id === attendanceId) {
      this.currentAttendanceSub.next({
        ...this.currentAttendance,
        currentContactChannel: { ...this.currentAttendance.currentContactChannel, isBlocked },
        isContactBlocked: isBlocked,
      });
    }

    this.blockedListRefreshSub.next();
  }

  private _currentSocketConnectionId?: string;

  //Values
  public get currentSocketConnectionId() {
    return this._currentSocketConnectionId;
  }

  public get currentAttendance() {
    return this.currentAttendanceSub.getValue();
  }

  public get currentConnectionStatus() {
    return this.connectionStatusSub.getValue();
  }

  public get attendanceToLoad() {
    return this.attendanceToLoad$.getValue();
  }

  public get userHasWhatsappPermission() {
    return this.userHasWhatsappPermissionSub.getValue();
  }

  public get userHasWhatsappNumberPermission() {
    return this.userHasWhatsappNumberPermissionSub.getValue();
  }

  public get languagesCompany(){
    return this.languagesCompanySub.getValue();
  }

  public get userHasOnlyOneCompany() {
    return this.userHasOnlyOneCompanySub.getValue();
  }

  public get appBarCollapsed() {
    return this.appBarCollapsedSub.getValue();
  }

  public get blockContactFeatureEnabled() {
    return this.blockContactFeatureEnabledSub.getValue();
  }

  public get kanbanWaitingTimeEnabled() {
    return this.kanbanWaitingTimeEnabledSub.getValue();
  }

  public get currentHistory() {
    return this.currentHistorySub.getValue();
  }
  public get currentHistories() {
    return this.currentHistoriesSub.getValue();
  }

  private get attendances() {
    return this.attendancesSub.getValue();
  }

  private get attendanceHistoryAdapter():
    | AttendanceHistoryAdapterService
    | undefined {
    return this.injector.get(AttendanceHistoryAdapterService); //TO AVOID CICLIC DEPENDENCY
  }

  private get ctaUnreadAttendanceTitle(): CtaUnreadAttendanceTitleService {
    return this.injector.get(CtaUnreadAttendanceTitleService);
  }

  public setAttendanceToLoad(attendanceToLoad?: {type: 'previous' | 'next', attendanceId: string, page?: number}) {
    this.attendanceToLoad$.next(attendanceToLoad);
  }

  //Public methods
  public setSocketConnectionId(connectionId: string) {
    this._currentSocketConnectionId = connectionId;
    this.socketConnectionIdSub.next(connectionId);
    console.log('[LiveChatStateService] ✅ Socket Connection ID SET:', connectionId);
  }

  public resetSocketConnectionId() {
    this._currentSocketConnectionId = undefined;
    this.socketConnectionIdSub.next(undefined);
    console.warn('[LiveChatStateService] ⚠️ Socket Connection ID RESET (undefined)');
  }

  public setConnectionStatus(status: string | null) {
    this.connectionStatusSub.next(status);
  }

  public markConnectionEstablished(): void {
    this.connectionEstablishedSub.next(true);
  }

  public addOldHistoryMessages(
    isNewAttendance: boolean,
    isPreviousMessages = false,
    ...items: AttendanceMessage[]
  ) {
    const currentValue = this.currentHistoriesSub.value.histories;
    const olderMessages = this.attendanceHistoryAdapter!.adapt(items);

    if (olderMessages.length === 0) return;

    const newState = [...currentValue];
    const isEmpty = newState.length === 0;

    if (isNewAttendance || isEmpty) {
      if(isPreviousMessages){
        newState.push(this.sortByDisplayOrder(olderMessages));
      }else{
        newState.unshift(this.sortByDisplayOrder(olderMessages));
      }
    } else {
      let lastIndex = newState.length - 1;
      if(!isPreviousMessages){
        lastIndex = 0
      }

      if (!newState[lastIndex]) newState[lastIndex] = [];

      if (isPreviousMessages) {
        newState[lastIndex].unshift(...olderMessages);
      } else {
        newState[lastIndex].push(...olderMessages);
      }

      newState[lastIndex] = this.sortByDisplayOrder(newState[lastIndex]);
    }

    this.currentHistoriesSub.next({
      histories: newState,
      event: EventTypeHistory.OLD_MESSAGES,
    });
  }

  public addNewHistoryMessages(
    attendanceSelectedId?: string,
    ...items: AttendanceMessage[]
  ) {
    if (
      attendanceSelectedId !== this.attendanceToLoad &&
      attendanceSelectedId !== this.currentAttendance?.id
    )
      return;

    const currentValue = this.currentHistoriesSub.value.histories;
    const findIndex = currentValue.findIndex((messages) =>
      messages.some((message) => message.attendanceId === attendanceSelectedId)
    );
    if (findIndex < 0) return;
    const currentHistory = currentValue[findIndex];

    items = this.attendanceHistoryAdapter!.adapt(items);
    currentValue[findIndex] = this.insertItemsByDisplayOrder(
      currentHistory || [],
      items
    );
    this.currentHistoriesSub.next({
      histories: [...currentValue],
      event: EventTypeHistory.NEW_MESSAGE,
    });
  }

  private isDisplayOrderingEnabled(): boolean {
    return this.featureToggleService.companyCanAccessFromCache(
      FeatureToggles.ENABLE_PLATFORM_TIMESTAMP_ORDERING
    );
  }

  private sortByDisplayOrder(
    history: AttendanceMessage[]
  ): AttendanceMessage[] {
    if (!this.isDisplayOrderingEnabled()) return history;

    const sorted: AttendanceMessage[] = [];
    let run: AttendanceMessage[] = [];

    const flushRun = () => {
      run.sort((a, b) => this.compareByDisplayOrder(a, b));
      sorted.push(...run);
      run = [];
    };

    history.forEach((item) => {
      if (this.isSortableMessage(item)) {
        run.push(item);
      } else {
        flushRun();
        sorted.push(item);
      }
    });
    flushRun();

    return sorted;
  }

  private insertItemsByDisplayOrder(
    history: AttendanceMessage[],
    items: AttendanceMessage[]
  ): AttendanceMessage[] {
    if (!this.isDisplayOrderingEnabled()) return [...history, ...items];

    const newHistory = [...history];

    items.forEach((item) => {
      const insertAt = this.findDisplayPositionFor(item, newHistory);
      newHistory.splice(insertAt, 0, item);
    });

    return newHistory;
  }

  private findDisplayPositionFor(
    item: AttendanceMessage,
    history: AttendanceMessage[]
  ): number {
    const endOfList = history.length;

    if (!this.isSortableMessage(item)) {
      return endOfList;
    }

    let position = endOfList;

    while (position > 0) {
      const messageAbove = history[position - 1];

      if (!this.isSortableMessage(messageAbove)) break;
      if (!this.wasSentAfter(messageAbove, item)) break;

      position--;
    }

    return position;
  }

  private isSortableMessage(item: AttendanceMessage): boolean {
    if (!item.id) return false;

    if (item.type !== undefined && item.type !== AttendanceMessageType.MESSAGE)
      return false;

    // Only proven-inbound messages reorder; outbound replies (Sophia or an
    // attendant) are barriers so a question never crosses its answer.
    if (item.data?.isSender !== false) return false;

    return !Number.isNaN(Date.parse(item.platformTimestamp || item.createdAt));
  }

  /** true when `a` was sent after `b`, by the display-order key. */
  private wasSentAfter(a: AttendanceMessage, b: AttendanceMessage): boolean {
    return this.compareByDisplayOrder(a, b) > 0;
  }

  private compareByDisplayOrder(
    a: AttendanceMessage,
    b: AttendanceMessage
  ): number {
    const KEEP_CURRENT_POSITIONS = 0;

    const aSentAt = Date.parse(a.platformTimestamp || a.createdAt);
    const bSentAt = Date.parse(b.platformTimestamp || b.createdAt);

    if (Number.isNaN(aSentAt) || Number.isNaN(bSentAt)) {
      return KEEP_CURRENT_POSITIONS;
    }

    if (aSentAt !== bSentAt) {
      return aSentAt - bSentAt;
    }

    return this.compareByServerId(a, b);
  }


  private compareByServerId(
    a: AttendanceMessage,
    b: AttendanceMessage
  ): number {
    try {
      const aId = BigInt(a.id!);
      const bId = BigInt(b.id!);

      if (aId === bId) return 0;

      return aId < bId ? -1 : 1;
    } catch {
      return 0;
    }
  }

  public updateAttendanceHistory(...items: AttendanceMessage[]) {
    const currentValue = this.currentHistoriesSub.value.histories;
    const currentHistory = currentValue[0] || [];

    items = this.attendanceHistoryAdapter!.adapt(items);
    items.forEach((item) => {
      const findIndex = (msg: AttendanceMessage) => {
        const sameId = item.id && item.id === msg.id;
        const sameFrontendMessageId =
          item.frontendMessageId &&
          item.frontendMessageId === msg.frontendMessageId;

        return sameId || sameFrontendMessageId;
      };
      const currentHistoriesMessageIndex = currentHistory.findIndex(findIndex);

      if (currentHistoriesMessageIndex >= 0) {
        currentHistory[currentHistoriesMessageIndex] = item;
      }
    });

    if (this.currentAttendance?.id) {
      currentValue[0] = currentHistory;
      this.currentHistoriesSub.next({
        histories: [...currentValue],
        event: EventTypeHistory.UPDATE_MESSAGE,
      });
    }
  }

  public resetHistory(eventType: EventTypeHistory = EventTypeHistory.OLD_MESSAGES) {
    this.currentHistoriesSub.next({ histories: [], event: eventType });
  }

  public addAttendanceToStart(attendance: Attendance) {
    const idx = this.attendances.findIndex(
      (_attendance) => _attendance.id === attendance.id
    );
    if (idx >= 0) return;

    const enriched = this.enrichWithBlockedState(attendance);
    this.attendancesSub.next([enriched, ...this.attendances]);
    this.ctaUnreadAttendanceTitle.addUnreadAttendance(attendance.id);
  }

  public addAttendancesToEnd(...attendances: Array<Attendance>) {
    const enriched = attendances.map(a => this.enrichWithBlockedState(a));
    this.attendancesSub.next([...this.attendances, ...enriched]);
  }

  public updateAttendanceListItem(updatedAttendance: Attendance) {
    const idx = this.attendances.findIndex(
      (attendance) => attendance.id === updatedAttendance.id
    );
    const existingAttendance = this.attendances[idx];
    const mergedAttendance = {
      ...updatedAttendance,
      isContactBlocked: existingAttendance?.isContactBlocked ?? updatedAttendance.isContactBlocked
    };
    this.attendances[idx] = mergedAttendance;

    this.attendancesSub.next([...this.attendances]);
    this.ctaUnreadAttendanceTitle.handleAttendanceUpdate(mergedAttendance);
    this.updateCurrentAttendance(mergedAttendance);
  }

  public updateAttendanceListUp(updatedAttendance: Attendance) {
    const existingAttendance = this.attendances.find(
      (attendance) => attendance.id === updatedAttendance.id
    );
    const mergedAttendance = {
      ...updatedAttendance,
      isContactBlocked: existingAttendance?.isContactBlocked ?? updatedAttendance.isContactBlocked
    };
    const filteredAttendances = this.attendances.filter(
      (attendance) => attendance.id !== updatedAttendance.id
    );
    this.attendancesSub.next([mergedAttendance, ...filteredAttendances]);
    this.ctaUnreadAttendanceTitle.handleAttendanceUpdateListUp(mergedAttendance, this.currentAttendance);
    this.updateCurrentAttendance(mergedAttendance);
  }

  public updateCurrentAttendance(updatedAttendance: Attendance) {
    if (this.currentAttendance?.id === updatedAttendance.id) {
      this.setCurrentAttendance(updatedAttendance);
    }
  }

  public removeAttendance(removedAttendance: Attendance) {
    const filteredAttendances = this.attendances.filter(
      (attendance) => attendance.id !== removedAttendance.id
    );
    this.attendancesSub.next([...filteredAttendances]);

    const isRemovingOpenAttendance =
      this.currentAttendance?.id === removedAttendance.id;
    if (
      isRemovingOpenAttendance &&
      this.hasNoAccessToCompany(removedAttendance)
    ) {
      this.setCurrentAttendance(undefined);
      this.chatNoAccessSub.next(true);
      return;
    }

    this.updateCurrentAttendance(removedAttendance);
  }

  private hasNoAccessToCompany(attendance: Attendance): boolean {
    const companyId = attendance.currentCompany?.id;
    if (!companyId) return false;

    return this.companyAccessService.isCompanyOutsideUserAccess(companyId);
  }

  public clearAttendances() {
    this.attendancesSub.next([]);
  }

  public setCurrentAttendance(attendance?: Attendance) {
    this.chatNoAccessSub.next(false);
    this.attendanceToLoad$.next(undefined);
    if (attendance) {
      const enriched = this.enrichWithBlockedState(attendance);
      this.currentAttendanceSub.next(
        { ...this.currentAttendance, ...enriched }
      );
    } else {
      const previousAttendance = this.currentAttendance;
      if (previousAttendance) {
        this.ctaUnreadAttendanceTitle.handleAttendanceUpdate(previousAttendance);
      }
      this.currentAttendanceSub.next(undefined);
    }
    this.isWriting = !!attendance;
  }

  public setFocusedAttendance(attendanceId?: string){
    this.currentAttendanceSub.next({
      ...this.currentAttendance,
      focusedAttendanceId: this.currentAttendance!.id === attendanceId? undefined : attendanceId
    } as Attendance)
  }

  public addSetCurrentAttendanceInterceptor(
    fn: (main: Attendance | undefined) => Attendance | undefined
  ) {
    this.currentAttendanceSub.addInterceptor(fn)
  }

  public removeSetCurrentAttendanceInterceptor(
    fn: (main: Attendance | undefined) => Attendance | undefined
  ) {
    this.currentAttendanceSub.removeInterceptor(fn)
  }

  public setUserHasWhatsappPermission(hasWhatsapp: boolean) {
    this.userHasWhatsappPermissionSub.next(hasWhatsapp);
  }

  public setUserHasWhatsappNumberPermission(hasWhatsapp: boolean) {
    this.userHasWhatsappNumberPermissionSub.next(hasWhatsapp);
  }

  public setLanguagesCompany(language:any){
    this.languagesCompanySub.next(language)
  }

  public setUserHasPhonePermission(hasPhone: boolean) {
    this.userHasPhonePermissionSub.next(hasPhone);
  }

  public setUserHasOnlyOneCompany(hasOnlyOneCompany: boolean) {
    this.userHasOnlyOneCompanySub.next(hasOnlyOneCompany);
  }

  public setAppBarCollapsed(collapsed: boolean) {
    this.appBarCollapsedSub.next(collapsed);
  }

  public setBlockContactFeatureEnabled(enabled: boolean) {
    this.blockContactFeatureEnabledSub.next(enabled);
  }

  public setBlockAttendanceExport(enabled: boolean) {
    this.blockAttendanceExportSub.next(enabled);
  }

  public setKanbanWaitingTimeEnabled(enabled: boolean) {
    this.kanbanWaitingTimeEnabledSub.next(enabled);
  }

  public get isExportingPdf() {
    return this.isExportingPdfSub.getValue();
  }

  public setIsExportingPdf(exporting: boolean) {
    this.isExportingPdfSub.next(exporting);
  }

  public get currentPhoneAppStatus(): PhoneStatus | undefined {
    return this.phoneAppStatus.getValue();
  }

  public setPhoneStatus(status: PhoneStatus | undefined) {
    this.phoneAppStatus.next(status);
  }

  public get currentPhoneCallStatus(): CallStatus | undefined {
    return this.phoneCallStatus.getValue();
  }

  public setPhoneCallStatus(status: CallStatus | undefined) {
    this.phoneCallStatus.next(status);
  }

  public resetPhoneCallStatus() {
    this.phoneCallStatus.next(undefined);
  }
}
